// Matching an appointment's recorded type back to one of the practice's services.
//
// The Edit dialog was telling people that "lab_draw_+_ekg" is "not one of the
// practice's services". The practice has a service called **Lab Draw + EKG**. It
// is the same thing, written differently.
//
// We do this to ourselves. Three places write the type as a slug —
//
//   aiAppointmentScheduling.ts   "Lab Draw + EKG" -> lab-draw-+-ekg
//   routes/booking.ts            "Lab Draw + EKG" -> lab-draw-+-ekg
//   AppointmentModal / QuickSchedule   -> lab_draw_+_ekg
//
// — and three other places matched with plain `toLowerCase()` equality, which
// none of those slugs can ever satisfy. An appointment booked through our own UI
// came back unrecognised by our own UI.
//
// So separators and punctuation are noise, and the comparison ignores them. This
// is deliberately not fuzzy matching: "Lab visit" still does not become
// "Laboratory Draw", because those are genuinely different services and guessing
// between them would put a patient in the wrong appointment.
export function serviceKey(name: string | null | undefined): string {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export interface NamedService {
  id: string;
  name: string;
}

// The service an appointment's recorded type refers to, or undefined when the
// practice genuinely has no such service configured — which is the common case
// for the Practice Fusion import ("Wellness Exam", "Follow-Up Visit").
export function findServiceByName<T extends NamedService>(
  services: T[],
  recorded: string | null | undefined,
): T | undefined {
  const key = serviceKey(recorded);
  if (!key) return undefined;
  return (services || []).find((svc) => serviceKey(svc.name) === key);
}
